import { AlertCircle, Info, type LucideIcon } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type StateProps = {
  title: string;
  description?: ReactNode;
  icon?: LucideIcon;
  action?: {
    label: string;
    onClick: () => void;
    variant?: "default" | "outline";
  };
  secondaryAction?: { label: string; onClick: () => void };
  className?: string;
};

/** Centered empty / info state for a section. */
export const PanelEmpty: FC<StateProps> = ({
  title,
  description,
  icon = Info,
  action,
  secondaryAction,
  className
}) => (
  <EmptyState
    icon={icon}
    title={title}
    description={description}
    className={className}
    action={
      action || secondaryAction ? (
        <>
          {action ? (
            <Button
              variant={action.variant === "outline" ? "secondary" : "primary"}
              size="sm"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button variant="ghost" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ) : null}
        </>
      ) : undefined
    }
  />
);

/** Centered error state with a retry. */
export const PanelError: FC<{
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}> = ({
  title = "Couldn't Load This Section",
  message,
  onRetry,
  retryLabel = "Try Again",
  className
}) => (
  <PanelEmpty
    title={title}
    description={message}
    icon={AlertCircle}
    action={
      onRetry
        ? { label: retryLabel, onClick: onRetry, variant: "outline" }
        : undefined
    }
    className={className}
  />
);

/** Centered spinner with a caption. */
export const PanelLoading: FC<{ text?: string; className?: string }> = ({
  text = "Loading...",
  className
}) => (
  <div
    className={cn(
      "flex flex-1 items-center justify-center gap-2 px-6 py-10 text-sm text-fg-3",
      className
    )}
    role="status"
  >
    <Spinner className="size-4" />
    <span>{text}</span>
  </div>
);

/** A few skeleton rows for list sections. */
export const PanelSkeletonRows: FC<{ rows?: number; className?: string }> = ({
  rows = 4,
  className
}) => (
  <div className={cn("flex flex-col gap-3 p-4", className)} aria-hidden>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton className="size-8 rounded-md" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);
